import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Users, MapPin, Mail, User, ArrowRight, CheckCircle, Phone } from 'lucide-react';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

const BookTrek = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prefilledTrek = searchParams.get('trek') || '';

  const [formData, setFormData] = useState({
    booking_type: 'Trek',
    trek_name: prefilledTrek,
    user_name: '',
    user_email: '',
    phone_number: '',
    date: '',
    guests: 1
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [treks, setTreks] = useState([]);

  useEffect(() => {
    // Fetch treks for the dropdown
    fetch(`${API_URL}/api/treks`)
      .then(res => res.json())
      .then(data => setTreks(data))
      .catch(err => console.error(err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'user_name' && /[^a-zA-Z\s]/.test(value)) {
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setSuccess(true);
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to submit booking');
      }
    } catch (err) {
      setError('Network error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', padding: '2rem' }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }}
          style={{ backgroundColor: 'white', padding: '4rem 3rem', borderRadius: '16px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', textAlign: 'center', maxWidth: '500px' }}
        >
          <div style={{ color: '#ea580c', marginBottom: '1.5rem', display: 'flex', justifyContent: 'center' }}>
            <CheckCircle size={64} />
          </div>
          <h2 style={{ fontSize: '2rem', color: '#111', marginBottom: '1rem' }}>Booking Received!</h2>
          <p style={{ color: '#6b7280', fontSize: '1.1rem', marginBottom: '2rem', lineHeight: '1.6' }}>
            Thank you, {formData.user_name}. Your trek booking for <strong>{formData.trek_name}</strong> on {formData.date} has been submitted successfully. Our team will contact you shortly to confirm the details.
          </p>
          <button 
            onClick={() => navigate('/')}
            style={{ padding: '12px 24px', backgroundColor: '#ea580c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 auto' }}
          >
            Return to Home <ArrowRight size={18} />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '80vh', backgroundColor: '#f9fafb', padding: '4rem 2rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ fontSize: '2.5rem', color: '#ea580c', marginBottom: '0.5rem' }}>Book Your Trek</h1>
          <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>Embark on an unforgettable adventure into the wild mountains.</p>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ backgroundColor: 'white', padding: '3rem', borderRadius: '16px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}
        >
          {error && (
            <div style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '1rem', borderRadius: '8px', marginBottom: '2rem', textAlign: 'center' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1.5rem' }}>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>Full Name</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '12px', left: '12px', color: '#9ca3af' }}><User size={20} /></div>
                  <input required type="text" name="user_name" value={formData.user_name} onChange={handleChange} placeholder="John Doe" style={{ width: '100%', padding: '12px 12px 12px 42px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', outline: 'none' }} />
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>Email Address</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '12px', left: '12px', color: '#9ca3af' }}><Mail size={20} /></div>
                  <input required type="email" name="user_email" value={formData.user_email} onChange={handleChange} placeholder="john@example.com" style={{ width: '100%', padding: '12px 12px 12px 42px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', outline: 'none' }} />
                </div>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>Phone Number</label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '12px', left: '12px', color: '#9ca3af', zIndex: 2 }}><Phone size={20} /></div>
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={formData.phone_number}
                  onChange={(value) => setFormData(prev => ({ ...prev, phone_number: value }))}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>Select Trek</label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '12px', left: '12px', color: '#9ca3af' }}><MapPin size={20} /></div>
                <select required name="trek_name" value={formData.trek_name} onChange={handleChange} style={{ width: '100%', padding: '12px 12px 12px 42px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', outline: 'none', backgroundColor: 'white', appearance: 'none' }}>
                  <option value="" disabled>Select a trek</option>
                  {treks.map(t => (
                    <option key={t.id} value={t.name}>{t.name}</option>
                  ))}
                  {/* Fallback if treks haven't loaded but we have a prefilled value */}
                  {prefilledTrek && !treks.find(t => t.name === prefilledTrek) && (
                    <option value={prefilledTrek}>{prefilledTrek}</option>
                  )}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>Date of Visit</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '12px', left: '12px', color: '#9ca3af' }}><Calendar size={20} /></div>
                  <input required type="date" name="date" value={formData.date} onChange={handleChange} style={{ width: '100%', padding: '12px 12px 12px 42px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', outline: 'none' }} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>Number of Guests</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '12px', left: '12px', color: '#9ca3af' }}><Users size={20} /></div>
                  <input required type="number" min="1" max="20" name="guests" value={formData.guests} onChange={handleChange} style={{ width: '100%', padding: '12px 12px 12px 42px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', outline: 'none' }} />
                </div>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              style={{
                marginTop: '1rem',
                width: '100%',
                padding: '14px',
                backgroundColor: '#ea580c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Processing...' : 'Confirm Booking'}
            </button>

          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default BookTrek;
